import React from "react";
import { api, formatApiError } from "../../lib/apiClient";
import { INDIAN_STATES, sanitizeGstin } from "../../lib/constants";
import { UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SearchableSelect } from "../SearchableSelect";

// Customer/Vendor picker with "From Master" typeahead or inline URP/Cash customer details.
export function PartySelector({ form, setForm, cfg, parties, partyMode, setPartyMode, onQuickAdd, lookupPincode }) {
  const setQuickPartyOpen = (v) => { if (v) onQuickAdd(); };
  return (
<div>
  <div className="flex items-center justify-between mb-1.5">
    <div className="label-overline">{cfg.party === "vendor" ? "Vendor" : "Customer"} *</div>
    {cfg.party === "customer" && (
      <div className="flex items-center gap-1 text-xs">
        <button type="button" onClick={() => setPartyMode("existing")} data-testid="party-mode-existing" className={`px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-colors ${partyMode === "existing" ? "bg-brand text-white" : "bg-surface-muted text-ink-muted hover:bg-surface-hover"}`}>From Master</button>
        <button type="button" onClick={() => setPartyMode("urp")} data-testid="party-mode-urp" className={`px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-colors ${partyMode === "urp" ? "bg-brand text-white" : "bg-surface-muted text-ink-muted hover:bg-surface-hover"}`}>URP / Cash</button>
      </div>
    )}
  </div>
  {partyMode === "existing" ? (
    <div className="flex items-center gap-2">
      <SearchableSelect
        className="flex-1"
        testId="doc-party"
        options={parties.map(p => ({ value: p.id, label: p.name, sub: [p.phone, p.gstin, p.city].filter(Boolean).join(" · "), keywords: [p.phone, p.gstin, p.contact_person, p.city, p.email].filter(Boolean) }))}
        value={form.party_id}
        onChange={v => setForm({ ...form, party_id: v })}
        placeholder={`Type ${cfg.party} name, mobile or GSTIN…`}
      />
      <button type="button" onClick={() => setQuickPartyOpen(true)} className="btn-secondary inline-flex items-center gap-1 whitespace-nowrap" data-testid="quick-add-party-button" title={`Quick-add ${cfg.party}`}>
        <UserPlus size={14} weight="bold" /> New
      </button>
    </div>
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border border-dashed border-border rounded-md p-3 bg-surface-muted/40">
      <div className="sm:col-span-2 flex items-center gap-2">
        <span className="status-pill status-info">URP</span>
        <span className="text-xs text-ink-muted">Unregistered / Cash customer — details are captured on this invoice only.</span>
      </div>
      <div><div className="label-overline block mb-1">Name *</div><input className="field" value={form.party_inline.name} onChange={e => setForm({ ...form, party_inline: { ...form.party_inline, name: e.target.value } })} data-testid="urp-name" /></div>
      <div>
        <div className="label-overline block mb-1">GSTIN / URP</div>
        <div className="flex gap-1.5">
          <input className="field font-mono uppercase flex-1" value={form.party_inline.gstin} onChange={e => {
            const raw = e.target.value;
            const v = raw.trim().toUpperCase() === "URP" ? "URP" : sanitizeGstin(raw);
            setForm({ ...form, party_inline: { ...form.party_inline, gstin: v } });
          }} data-testid="urp-gstin-input" />
          <button type="button" onClick={async () => {
            const g = (form.party_inline.gstin || "").trim().toUpperCase();
            if (g.length !== 15 || g === "URP") { toast.error("Enter a 15-char GSTIN first"); return; }
            try {
              const { data } = await api.get(`/gstin/lookup/${g}`);
              setForm(f => ({
                ...f,
                party_inline: {
                  ...f.party_inline,
                  name: data.legal_name || data.trade_name || f.party_inline.name,
                  billing_address: data.address_line || f.party_inline.billing_address,
                  city: data.city || f.party_inline.city,
                  state: data.state || f.party_inline.state,
                  state_code: data.state_code || f.party_inline.state_code,
                  pincode: data.pincode || f.party_inline.pincode,
                },
              }));
              toast.success(`Auto-filled: ${data.legal_name}`);
            } catch (e) {
              toast.error(formatApiError(e?.response?.data?.detail) || "GSTIN lookup failed");
            }
          }} className="btn-secondary !px-2.5 text-xs whitespace-nowrap" data-testid="urp-gstin-lookup">Look up</button>
        </div>
      </div>
      <div><div className="label-overline block mb-1">Phone</div><input className="field" value={form.party_inline.phone} onChange={e => setForm({ ...form, party_inline: { ...form.party_inline, phone: e.target.value } })} data-testid="urp-phone" /></div>
      <div>
        <div className="label-overline block mb-1">State</div>
        <select className="field" value={form.party_inline.state_code} onChange={e => {
          const st = INDIAN_STATES.find(s => s.code === e.target.value);
          setForm({ ...form, party_inline: { ...form.party_inline, state_code: e.target.value, state: st?.name || "" } });
        }} data-testid="urp-state">
          <option value="">Select state</option>
          {INDIAN_STATES.map(s => <option key={s.code} value={s.code}>{s.code} - {s.name}</option>)}
        </select>
      </div>
      <div><div className="label-overline block mb-1">City</div><input className="field" list="indian-cities-list" value={form.party_inline.city} onChange={e => setForm({ ...form, party_inline: { ...form.party_inline, city: e.target.value } })} data-testid="urp-city" /></div>
      <div><div className="label-overline block mb-1">Pincode</div><input className="field font-mono" maxLength={6} value={form.party_inline.pincode || ""} onChange={e => {
        const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
        setForm({ ...form, party_inline: { ...form.party_inline, pincode: v } });
        if (v.length === 6) lookupPincode(v);
      }} data-testid="urp-pincode" placeholder="6-digit" /></div>
      <div className="sm:col-span-2"><div className="label-overline block mb-1">Billing Address</div><textarea rows={2} className="field" value={form.party_inline.billing_address} onChange={e => setForm({ ...form, party_inline: { ...form.party_inline, billing_address: e.target.value } })} data-testid="urp-address" /></div>
    </div>
  )}
</div>
  );
}
